import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarCheck, Check, ChevronLeft, ChevronRight, Globe, Phone } from 'lucide-react';
import { useTenantApiToken } from '../../contexts/TenantProfileContext';
import {
  discoveryCallSlotIsBookable,
  easternDateKey,
  easternZoneAbbrev,
  formatEasternClock,
  formatEasternLongDate,
  formatEasternMonthYear,
} from '../../utils/eastern-week';
import {
  bookQualificationCall,
  getQualificationCall,
  reopenQualificationCallForRebook,
  type QualificationCallDay,
  type QualificationCallState,
} from '../../services/tenantApi';
import { trackTenantGoogleAdsOnce } from '../../utils/googleAdsClick';
import { GOOGLE_ADS_EVENTS, GOOGLE_ADS_ONCE_KEYS } from '@shared/utils/google-ads';

const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
const DAY_CELL = 'flex aspect-square w-full items-center justify-center rounded-md text-sm';

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const next = new Date(year, month + delta, 1);
  return { year: next.getFullYear(), month: next.getMonth() };
}

function formatAppointmentLine(startAt: string, timeZone: string): string {
  return new Date(startAt).toLocaleString('en-US', {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function PrimaryContactWatermark({ name }: { name: string | null }) {
  const responsibility = name?.trim() ? `${name.trim()}'s responsibility` : null;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center overflow-hidden"
      aria-hidden="true"
    >
      <div className="origin-center -rotate-[18deg] scale-110 select-none text-center text-gray-700/80 dark:text-gray-100/70">
        <p className="whitespace-nowrap text-[clamp(1.35rem,3.2vw,3.25rem)] font-semibold tracking-wide">
          For primary contact use only.
        </p>
        {responsibility ? (
          <p className="mt-3 whitespace-nowrap text-[clamp(1.05rem,2.4vw,2.35rem)] font-semibold tracking-wide">
            {responsibility}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function SectionCheck({ done }: { done: boolean }) {
  if (!done) return null;
  return (
    <span
      className="absolute right-4 top-4 z-30 grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 sm:right-5 sm:top-5"
      aria-label="Complete"
    >
      <Check size={16} strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

function BookingSchedule({
  locked = false,
  state,
  selectedDay,
  selectedStartAt,
  error,
  booking,
  onSelectDate,
  onSelectSlot,
  onConfirm,
  now = Date.now(),
  videoWatched = true,
}: {
  locked?: boolean;
  state: QualificationCallState;
  selectedDay: QualificationCallDay | undefined;
  selectedStartAt: string | null;
  error: string | null;
  booking: boolean;
  onSelectDate: (date: string) => void;
  onSelectSlot: (startAt: string) => void;
  onConfirm: () => void;
  now?: number;
  videoWatched?: boolean;
}) {
  const timesRef = useRef<HTMLDivElement>(null);
  const slotIsOpen = (slot: { startAt: string; remaining: number }, date: string) =>
    slot.remaining > 0 && discoveryCallSlotIsBookable(slot.startAt, date, now);

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const day of state.days) {
      if (day.slots.some((slot) => slotIsOpen(slot, day.date))) {
        dates.add(day.date);
      }
    }
    return dates;
  }, [now, state.days]);
  const selectedDate = selectedDay?.date ?? null;
  const anchor = selectedDate ?? easternDateKey(new Date());
  const [viewYear, setViewYear] = useState(Number(anchor.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(Number(anchor.slice(5, 7)) - 1);

  useEffect(() => {
    if (!selectedDate) return;
    setViewYear(Number(selectedDate.slice(0, 4)));
    setViewMonth(Number(selectedDate.slice(5, 7)) - 1);
  }, [selectedDate]);

  const todayKey = easternDateKey(new Date());
  const zone = easternZoneAbbrev();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const cells: Array<number | null> = [];
  for (let index = 0; index < firstWeekday; index += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);

  const monthHasOpenDay = [...availableDates].some((key) => {
    return Number(key.slice(0, 4)) === viewYear && Number(key.slice(5, 7)) - 1 === viewMonth;
  });
  const dayKey = selectedDay?.date ?? selectedDate ?? '';
  const slots = (selectedDay?.slots ?? []).filter((slot) => {
    if (new Date(slot.startAt).getTime() <= now) return false;
    return discoveryCallSlotIsBookable(slot.startAt, dayKey, now) || slot.remaining === 0;
  });
  const openSlotCount = slots.filter((slot) => slotIsOpen(slot, dayKey)).length;
  const availabilityLabel =
    openSlotCount === 0
      ? null
      : openSlotCount === 1
        ? '1 call slot available'
        : `${openSlotCount} call slots available`;

  const chooseDate = (dateKey: string) => {
    onSelectDate(dateKey);
    if (window.matchMedia('(max-width: 1023px)').matches) {
      window.setTimeout(() => {
        timesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  };

  const schedule = (
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Select a Date & Time</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {state.durationMinutes}-minute call
        {state.phoneDisplay ? `. We will call you at ${state.phoneDisplay}.` : '.'}
      </p>
      {state.days.length === 0 ? (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          There are no open times right now. Please try again later.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="min-w-0 lg:flex-[1.85]">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {formatEasternMonthYear(viewYear, viewMonth)}
              </p>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => {
                    const next = shiftMonth(viewYear, viewMonth, -1);
                    setViewYear(next.year);
                    setViewMonth(next.month);
                  }}
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = shiftMonth(viewYear, viewMonth, 1);
                    setViewYear(next.year);
                    setViewMonth(next.month);
                  }}
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1.5">
              {DAY_HEADERS.map((label) => (
                <div key={label} className="pb-1 text-center text-[11px] font-medium text-gray-400 dark:text-gray-500">
                  {label}
                </div>
              ))}
              {cells.map((day, index) => {
                if (day === null) return <div key={`empty-${index}`} className={DAY_CELL} />;
                const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const hasSlots = availableDates.has(dateKey);
                const isSelected = dateKey === selectedDate;
                const isToday = dateKey === todayKey;
                if (!hasSlots) {
                  return (
                    <span
                      key={dateKey}
                      className={`${DAY_CELL} ${
                        isToday ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {day}
                    </span>
                  );
                }
                return (
                  <button
                    key={dateKey}
                    type="button"
                    aria-pressed={isSelected}
                    aria-label={formatEasternLongDate(dateKey)}
                    onClick={() => chooseDate(dateKey)}
                    className={`${DAY_CELL} bg-red-600 font-semibold text-white hover:bg-red-700 ${
                      isSelected ? 'ring-2 ring-inset ring-white' : ''
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {!monthHasOpenDay ? (
              <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">No open days this month.</p>
            ) : null}

            <p className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Globe className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
              Eastern Time ({zone})
            </p>
          </div>

          <div ref={timesRef} className="min-w-0 flex-1 border-t border-gray-100 pt-5 dark:border-white/5 lg:min-w-[12.5rem] lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <h3 className="text-base font-medium text-gray-900 dark:text-white" aria-live="polite">
              {selectedDate ? formatEasternLongDate(selectedDate) : 'Select a date'}
            </h3>
            {error && !locked ? (
              <p role="status" className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30">
                {error}
              </p>
            ) : null}
            {availabilityLabel ? <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{availabilityLabel}</p> : null}
            {slots.length > 0 ? (
              <div className="mt-4 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <span>Call time</span>
                <span>Call slots</span>
              </div>
            ) : null}
            <div className="mt-2 max-h-[26.5rem] space-y-2 overflow-y-auto pr-1">
              {slots.map((slot) => {
                const timeLabel = formatEasternClock(slot.startAt);
                const open = slotIsOpen(slot, dayKey);
                const tooSoon = open && !videoWatched && new Date(slot.startAt).getTime() < now + VIDEO_PREREQUISITE_MS;
                const pending = !locked && open && !tooSoon && selectedStartAt === slot.startAt;
                if (!open) {
                  return (
                    <div
                      key={slot.startAt}
                      className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3.5 text-sm dark:border-white/10 dark:bg-white/5"
                    >
                      <span className="whitespace-nowrap font-medium text-gray-400 line-through dark:text-gray-500">
                        {timeLabel}
                      </span>
                      <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Taken
                      </span>
                    </div>
                  );
                }
                if (tooSoon) {
                  return (
                    <button
                      key={slot.startAt}
                      type="button"
                      disabled
                      className="flex w-full cursor-not-allowed items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3.5 text-sm text-gray-400"
                    >
                      <span className="whitespace-nowrap">{timeLabel}</span>
                      <span className="text-xs font-semibold">Not enough time to watch the video</span>
                    </button>
                  );
                }
                if (pending) {
                  return (
                    <div key={slot.startAt} className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => onSelectSlot('')}
                        className="w-[42%] rounded-lg border border-red-600 py-3 text-sm font-semibold text-red-700 dark:text-red-400"
                      >
                        {timeLabel}
                      </button>
                      <button
                        type="button"
                        disabled={booking}
                        onClick={onConfirm}
                        className="flex-1 rounded-lg bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        {booking ? 'Booking…' : error ? error : 'Confirm'}
                      </button>
                    </div>
                  );
                }
                return (
                  <button
                    key={slot.startAt}
                    type="button"
                    disabled={booking}
                    onClick={() => onSelectSlot(slot.startAt)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-red-600 px-3 py-3.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60 dark:text-red-400 dark:hover:bg-red-950/50"
                  >
                    <span className="whitespace-nowrap">{timeLabel}</span>
                    <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide">Available</span>
                  </button>
                );
              })}
              {selectedDate && openSlotCount === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {slots.length === 0 ? 'No open times on this day.' : 'All call slots are taken on this day.'}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!locked) return schedule;

  return (
    <fieldset
      disabled
      className="m-0 min-w-0 cursor-not-allowed border-0 p-0"
      aria-label="Booking calendar, available to the primary contact"
    >
      {schedule}
    </fieldset>
  );
}

const VIDEO_PREREQUISITE_MS = (10 * 60 + 57) * 1000;

export default function QualificationCallBooking({
  className = '',
  onAccessChange,
  videoWatched = false,
}: {
  className?: string;
  onAccessChange?: (access: { callShowed: boolean; canBook: boolean }) => void;
  videoWatched?: boolean;
}) {
  const getToken = useTenantApiToken();
  const [state, setState] = useState<QualificationCallState | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedStartAt, setSelectedStartAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const next = await getQualificationCall(getToken);
        if (!cancelled) {
          setState(next);
          const firstDate = next.days[0]?.date ?? null;
          setSelectedDate(firstDate);
          setSelectedStartAt(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load available times.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (loading || !state) return;
    onAccessChange?.({ callShowed: state.discoveryCallShowed === true, canBook: state.canBook });
  }, [loading, onAccessChange, state]);

  const selectedDay = useMemo(() => {
    if (!state?.days.length) return undefined;
    if (selectedDate) {
      const match = state.days.find((day) => day.date === selectedDate);
      if (match) return match;
    }
    return state.days[0];
  }, [state, selectedDate]);

  const selectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedStartAt(null);
  };

  const startRebook = async () => {
    setReopening(true);
    setError(null);
    try {
      const next = await reopenQualificationCallForRebook(getToken);
      setState(next);
      const firstDate = next.days[0]?.date ?? null;
      setSelectedDate(firstDate);
      setSelectedStartAt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to change your call time.');
    } finally {
      setReopening(false);
    }
  };

  const confirm = async () => {
    if (!selectedStartAt) return;
    setBooking(true);
    setError(null);
    try {
      const next = await bookQualificationCall(getToken, selectedStartAt);
      setState(next);
      setSelectedStartAt(null);
      trackTenantGoogleAdsOnce(GOOGLE_ADS_ONCE_KEYS.DISCOVERY_CALL_BOOKED, GOOGLE_ADS_EVENTS.DISCOVERY_CALL_BOOKED);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to book that time.');
      try {
        const refreshed = await getQualificationCall(getToken);
        setState(refreshed);
        setSelectedStartAt(null);
      } catch {
        // Keep the booking error visible if refresh fails.
      }
    } finally {
      setBooking(false);
    }
  };

  const timezone = state?.timezone ?? 'America/New_York';
  const locked = Boolean(state && !state.canBook);

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-bento dark:border-white/10 dark:bg-gray-800 ${className}`.trim()}
      aria-label={locked ? 'Discovery call, for the primary contact' : undefined}
    >
      <SectionCheck done={state?.discoveryCallShowed === true} />
      <div className={`p-4 sm:p-5 ${locked ? 'pointer-events-none select-none opacity-40 grayscale' : ''}`.trim()} aria-hidden={locked || undefined}>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-red-700" />
          </div>
        )}

        {!loading && error && !state && (
          <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/40 p-3 text-sm font-medium text-red-800">
            {error}
          </div>
        )}

        {!loading && state && !state.canBook && state.discoveryCallShowed && !state.appointment && (
          <div className="rounded-xl bg-emerald-50 px-4 py-5 text-center sm:px-6">
            <CalendarCheck className="mx-auto h-7 w-7 text-emerald-700" />
            <p className="mt-3 text-base font-semibold text-emerald-950">Discovery call complete</p>
            <p className="mt-3 text-sm text-emerald-900">
              {state.leadName
                ? `${state.leadName}, the primary contact, completed the discovery call.`
                : 'The primary contact completed the discovery call.'}
            </p>
          </div>
        )}

        {!loading && state && !state.canBook && state.appointment && (
          <div className="rounded-xl bg-emerald-50 px-4 py-5 text-center sm:px-6">
            <CalendarCheck className="mx-auto h-7 w-7 text-emerald-700" />
            <p className="mt-3 text-base font-semibold text-emerald-950">Call booked</p>
            <p className="mt-1 text-sm text-emerald-900">
              {formatAppointmentLine(state.appointment.startAt, state.appointment.timezone || timezone)}
            </p>
            <p className="mt-3 text-sm text-emerald-900">
              {state.leadName
                ? `${state.leadName}, the primary contact, booked this call.`
                : 'The primary contact booked this call.'}
            </p>
          </div>
        )}

        {!loading && state && !state.canBook && !state.appointment && !state.discoveryCallShowed && (
          <BookingSchedule
            locked
            state={state}
            selectedDay={selectedDay}
            selectedStartAt={selectedStartAt}
            error={error}
            booking={false}
            onSelectDate={() => {}}
            onSelectSlot={() => {}}
            onConfirm={() => {}}
            now={now}
            videoWatched={videoWatched}
          />
        )}

        {!loading && state?.canBook && state.appointment && (
          <div className="flex min-h-[32rem] flex-col justify-center rounded-xl bg-emerald-50 px-4 py-5 text-center sm:px-6">
            <CalendarCheck className="mx-auto h-7 w-7 text-emerald-700" />
            <p className="mt-3 text-base font-semibold text-emerald-950">You are booked</p>
            <p className="mt-1 text-sm text-emerald-900">
              {formatAppointmentLine(state.appointment.startAt, state.appointment.timezone || timezone)}
            </p>
            {state.phoneDisplay && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-900">
                <Phone className="h-4 w-4" />
                We will call you at {state.phoneDisplay}.
              </p>
            )}
            <button
              type="button"
              disabled={reopening}
              onClick={() => void startRebook()}
              className="touch-optimized mt-5 w-full rounded-lg border border-emerald-200 bg-white dark:bg-gray-800 px-4 py-3 text-sm font-semibold text-emerald-900 hover:bg-emerald-100/60 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {reopening ? 'Loading times…' : 'Pick a different time'}
            </button>
          </div>
        )}

        {!loading && state?.canBook && !state.appointment && (
          <>
            <BookingSchedule
              state={state}
              selectedDay={selectedDay}
              selectedStartAt={selectedStartAt}
              error={error}
              booking={booking}
              onSelectDate={selectDate}
              onSelectSlot={(startAt) => setSelectedStartAt(startAt || null)}
              onConfirm={() => void confirm()}
              now={now}
              videoWatched={videoWatched}
            />
            {state.discoveryCallShowed ? (
              <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100">
                Thanks for completing a discovery call with us. You can book another time with us above if you would like.
              </p>
            ) : null}
          </>
        )}
      </div>
      {locked ? <PrimaryContactWatermark name={state?.leadName ?? null} /> : null}
    </section>
  );
}
